// Class ratings for the detail dialog. Ported from the homepage's dmc_hp_schedule_modal.

import { escapeHtml } from './schedule-data';

/* /class-ratings returns the aggregate ALREADY BUILT server-side. It is the PII-free public
   route: no emails, reviewer names arrive pre-shortened to "Firstname L.".
     byInstructor[instructorId] = {
       reviews: [{ text, by }],                        4 and 5 star reviews with text, newest first
       all:     { avg, n },                            avg over ALL their 1 to 5 ratings
       classes: { [classId]: { avg, n, idx: [..] } }   idx points into reviews[]
     }
   ratingsByClass / reviewsByClass (keyed by class title) are the previous shape, read only
   when an older backend answers without byInstructor. `overall` is the site-wide fallback.
   Never append ?fresh=1: it bypasses the server cache on a public route. */
const RATINGS_URL =
  'https://dmc-central-database-356018239005.europe-west3.run.app/class-ratings';

let ratings = null;
let ratingsPromise = null;

export function loadRatings() {
  if (ratingsPromise) return ratingsPromise;
  ratingsPromise = fetch(RATINGS_URL, { method: 'GET', cache: 'no-store' })
    .then((r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .then((json) => {
      ratings = {
        byInstructor: (json && json.byInstructor) || null,
        ratingsByClass: (json && json.ratingsByClass) || {},
        reviewsByClass: (json && json.reviewsByClass) || {},
        overall: (json && json.overall) || null,
      };
      return ratings;
    })
    .catch((err) => {
      console.warn(
        '[dmc-live-schedule] ratings load failed:',
        err && err.message,
      );
      ratings = {
        byInstructor: null,
        ratingsByClass: {},
        reviewsByClass: {},
        overall: null,
      };
      return ratings;
    });
  return ratingsPromise;
}

function stars(avg) {
  const n = Math.max(0, Math.min(5, Math.round(avg || 0)));
  let out = '';
  for (let i = 0; i < 5; i++) {
    out +=
      `<svg class="${i < n ? 'on' : 'off'}" viewBox="0 0 24 24" aria-hidden="true">` +
      '<path d="M12 2.5l2.9 6.05 6.65.95-4.8 4.65 1.15 6.6L12 18.6 6.1 21.7l1.15-6.6L2.45 10.5l6.65-.95z"/></svg>';
  }
  return out;
}

const fmtScore = (avg) => (Math.round(avg * 10) / 10).toFixed(1);

/* The rating row for one class. '' until the ratings have loaded. Three rungs:
     1. the instructor shown on the card, for THIS class (keyed by the liveClasses doc id);
     2. the same instructor's reviews from their other classes (the label says so);
     3. the site-wide overall line, with nothing to expand.
   On a week with a substitute (date override) the card shows the substitute, so their
   reviews are the ones that apply. */
export function ratingRowHtml(cls, inst) {
  if (!cls || !ratings) return '';
  const { byInstructor, ratingsByClass, reviewsByClass, overall } = ratings;
  let per = null;
  let reviews = null;
  let label = null;

  const ib =
    byInstructor && cls.idInstructor ? byInstructor[cls.idInstructor] : null;
  if (ib) {
    const ic = cls.classId && ib.classes ? ib.classes[cls.classId] : null;
    if (ic && ic.idx && ic.idx.length && ib.reviews) {
      per = ic;
      reviews = ic.idx.map((i) => ib.reviews[i]).filter(Boolean);
    } else if (ib.reviews && ib.reviews.length && ib.all) {
      per = ib.all;
      reviews = ib.reviews;
      const first = inst && inst.firstName ? String(inst.firstName).trim() : '';
      if (first) {
        label =
          (reviews.length === 1
            ? '1 review for '
            : reviews.length.toLocaleString() + ' reviews for ') +
          escapeHtml(first);
      }
    }
  } else if (!byInstructor) {
    const legacyPer = ratingsByClass && ratingsByClass[cls.className];
    const legacyReviews =
      (reviewsByClass && reviewsByClass[cls.className]) || [];
    if (legacyPer && legacyReviews.length) {
      per = legacyPer;
      reviews = legacyReviews;
    }
  }

  if (per && reviews && reviews.length) {
    const n = reviews.length;
    if (!label) label = n === 1 ? '1 review' : n.toLocaleString() + ' reviews';
    const chev =
      '<svg class="detail-chev" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
    const list = reviews
      .map(
        (rv) =>
          '<div class="detail-review">' +
          `<p>&ldquo;${escapeHtml(rv.text)}&rdquo;</p>` +
          (rv.by ? `<cite>${escapeHtml(rv.by)}</cite>` : '') +
          '</div>',
      )
      .join('');
    return (
      '<div class="detail-rating">' +
      `<span class="detail-stars">${stars(per.avg)}</span>` +
      `<span class="detail-rating-score">${fmtScore(per.avg)}</span>` +
      '<button type="button" class="detail-reviews-toggle" aria-expanded="false">' +
      `<span>${label}</span>${chev}</button>` +
      '</div>' +
      `<div class="detail-reviews">${list}</div>`
    );
  }

  if (overall && overall.count) {
    const ol =
      overall.count === 1
        ? '1 review'
        : overall.count.toLocaleString() + ' reviews';
    return (
      '<div class="detail-rating">' +
      `<span class="detail-stars">${stars(overall.avg)}</span>` +
      `<span class="detail-rating-score">${fmtScore(overall.avg)}</span>` +
      `<span class="detail-rating-count">${ol}</span>` +
      '</div>'
    );
  }
  return '';
}
